import {
  digestFrequencies,
  frequenciesUnlocked,
  sendTimes,
  timezones,
} from "@/config/horizonscanning/options";
import type { DigestFrequency, WizardState } from "@/config/horizonscanning/types";
import { cn } from "@/utils/cn";

interface FrequencyStepProps {
  state: WizardState;
  onChange: (patch: Partial<WizardState>) => void;
  /** When omitted (wizard signup), treated as unpaid: weekly only. */
  planStatus?: string | null;
  /** Hide time/timezone fields (e.g. watchlist cadence-only). */
  showSchedule?: boolean;
}

export function FrequencyStep({
  state,
  onChange,
  planStatus,
  showSchedule = true,
}: FrequencyStepProps) {
  const unlocked = frequenciesUnlocked(planStatus);

  const select = (value: DigestFrequency, locked: boolean) => {
    if (locked) return;
    onChange({ frequency: value });
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3" role="radiogroup" aria-label="Digest frequency">
        {digestFrequencies.map((f) => {
          const locked = f.requiresPaid && !unlocked;
          const selected = state.frequency === f.value;
          return (
            <button
              key={f.value}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-disabled={locked}
              disabled={locked}
              className={cn(
                "r1-choice",
                selected && "r1-choice-selected",
                locked && "cursor-not-allowed opacity-50 hover:border-slate-300 hover:bg-white",
              )}
              onClick={() => select(f.value, locked)}
            >
              <span className="flex flex-col text-left">
                <span className="font-medium">
                  {f.label}
                  {locked ? (
                    <span className="ml-2 text-sm font-normal text-ink-muted">(unlocks after you continue)</span>
                  ) : null}
                </span>
                <span className="text-sm text-ink-muted">{f.hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      {!unlocked ? (
        <p className="text-sm text-ink-muted">
          During your free week digests are weekly. Daily and bi-weekly unlock when you continue (€29 per source
          per month).
        </p>
      ) : null}
      {showSchedule ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium text-ink-soft">
            Time of day
            <select
              className="r1-field mt-1"
              value={state.sendTime}
              onChange={(e) => onChange({ sendTime: e.target.value })}
            >
              {sendTimes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-ink-soft">
            Timezone
            <select
              className="r1-field mt-1"
              value={state.timezone}
              onChange={(e) => onChange({ timezone: e.target.value })}
            >
              {timezones.map((z) => (
                <option key={z.value} value={z.value}>
                  {z.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
    </div>
  );
}
